import { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Snackbar,
  Alert,
  Container,
  Paper,
  Breadcrumbs,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { RootState, useAppSelector } from '../../../../state/state';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import React from 'react';
import { openModal } from '../../../../state/ModalSlice/ModalSlice';
import { useAppDispatch } from '../../../../state/state';
import { format, addDays, isPast } from 'date-fns';
import { db } from '../../../../Config/firebase';

interface Plan {
  id: string;
  plan: number;
  planId: string;
  name: string;
  price: number | null;
  features: string[];
  isDefault?: boolean;
  createdAt?: any;
  updatedAt?: any;
  graceDays?: number;
  duration?: number;
}

const StyledPlanCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(4),
  backgroundColor: 'white',
  width: '335px',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.12)',
    cursor: 'pointer',
  },
  position: 'relative',
  border: '1px solid rgba(0, 0, 0, 0.05)',
}));

const PlanFeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const PlanBadge = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.spacing(3),
  padding: theme.spacing(0.5, 2),
  display: 'inline-block',
  marginBottom: theme.spacing(2),
  fontWeight: 500,
  fontSize: '0.875rem',
  maxWidth: 'fit-content',
}));

const HighlightBadge = styled(Box)(() => ({
  backgroundColor: '#edf5ff',
  color: '#2563eb',
  borderRadius: '30px',
  padding: '4px 12px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 500,
  fontSize: '0.75rem',
  border: '1px solid #dbe9ff',
  whiteSpace: 'nowrap',
  height: '24px',
  lineHeight: 1,
}));

const YourPlanBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 20,
  right: -65,
  backgroundColor: '#2563eb',
  color: '#ffffff',
  padding: theme.spacing(0.5, 2),
  fontWeight: 600,
  fontSize: '0.75rem',
  zIndex: 2,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  transform: 'rotate(45deg)',
  transformOrigin: 'center',
  width: '200px',
  textAlign: 'center',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 100%)',
  }
}));

function SubscriptionPlanPage() {
  const userDetails = useAppSelector((state: RootState) => state.userSliceV2?.data);
  const dispatch = useAppDispatch();

  const [plans, setPlans] = useState<Plan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [userPlanId, setUserPlanId] = useState<string | null>(null);
  const [userPlanName, setUserPlanName] = useState<string | null>(null);
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [showExpiryAlert, setShowExpiryAlert] = useState<boolean>(false);
  const [showAdminAlert, setShowAdminAlert] = useState<boolean>(false);
  const [defaultPlanName, setDefaultPlanName] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [planDetailsUpdated, setPlanDetailsUpdated] = useState<boolean>(false);
  const [internationalMarkup, setInternationalMarkup] = useState<number | null>(null);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  const fetchPlans = async () => {
    try {
      if (!db) {
        console.error('Firebase database not initialized');
        setError('Database connection error');
        return getDefaultPlans();
      }

      const plansCollection = collection(db, 'Plans');
      const planSnapshot = await getDocs(plansCollection);

      if (planSnapshot.empty) {

        setError('No plans available - Showing default plans');
        return getDefaultPlans();
      }

      const transformedPlans = planSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          planId: data.planId,
          plan: data.plan,
          name: data.name,
          price: data.price !== undefined ? data.price : null,
          features: Array.isArray(data.features) ? data.features : [],
          isDefault: Boolean(data.isDefault),
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          graceDays: data.graceDays,
          duration: data.duration
        };
      }).sort((a, b) => a.plan - b.plan);

      setPlans(transformedPlans);
      setError(null);
      return transformedPlans;
    } catch (err) {
      console.error('Error fetching plans from Firestore:', err);
      setError('Error loading plan details - Showing default plans');
      return getDefaultPlans(); // Fallback to default plans
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    const determinePlan = async () => {
      if (plans.length === 0) return;

      const defaultPlan = plans.find(plan => plan.isDefault);
      if (defaultPlan) {
        setDefaultPlanName(defaultPlan.name);
      }

      if (!userDetails?.uid) {
        setUserPlanId(null);
        return;
      }

      try {
        const userDocRef = doc(db, 'ServiceProviders', userDetails?.uid || '');
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();

          // Get international markup from SP document
          if (userData.internationalMarkup !== undefined) {
            setInternationalMarkup(userData.internationalMarkup);
          }

          if (userData.planDetails?.planId) {
            setUserPlanId(userData.planDetails.planId);
            const userPlan = plans.find(plan => plan.id === userData.planDetails.planId);

            // Clear alerts if planDetails was updated
            if (userData.planDetails.updated || userData.pricingTable?.updated) {
              setPlanDetailsUpdated(true);
              setShowExpiryAlert(false);
              setShowAdminAlert(false);
            } else if (userPlan) {
              setUserPlanName(userPlan.name);

              // Check if the plan is Elite Pro or Elite Plus and has expired
              if ((userPlan.name === 'Elite Pro' || userPlan.name === 'Elite Plus') && userData.planDetails.expiryDate && !planDetailsUpdated) {
                const expiry = userData.planDetails.expiryDate.toDate ?
                  userData.planDetails.expiryDate.toDate() :
                  new Date(userData.planDetails.expiryDate);

                setExpiryDate(expiry);

                // Set grace days from the plan directly
                const planGraceDays = plans.find(plan => plan.name === 'Elite Pro' || plan.name === 'Elite Plus')?.graceDays || 0;

                // Calculate due date (expiry + grace days)
                const due = addDays(expiry, planGraceDays);
                setDueDate(due);

                // Check if today is the due date using Firebase timestamp format
                if (due && isPast(due)) {
                  setShowAdminAlert(true);
                  setShowExpiryAlert(false);
                } else if (isPast(expiry)) {
                  setShowExpiryAlert(true);
                  setShowAdminAlert(false);
                } else {
                  setShowExpiryAlert(false);
                  setShowAdminAlert(false);
                }
              } else {
                setShowExpiryAlert(false);
                setShowAdminAlert(false);
              }
            }
          } else if (defaultPlan) {
            // If no plan details found, set to default plan
            setUserPlanId(defaultPlan.id);
            setUserPlanName(defaultPlan.name);
          } else {
            setUserPlanId(null);
            setUserPlanName(null);
          }
        } else if (defaultPlan) {
          // If SP document not found, set to default plan
          setUserPlanId(defaultPlan.id);
          setUserPlanName(defaultPlan.name);
        } else {
          setUserPlanId(null);
          setUserPlanName(null);
        }
      } catch (err) {
        console.error('Error fetching user document:', err);
        if (defaultPlan) {
          // If error occurs, set to default plan
          setUserPlanId(defaultPlan.id);
          setUserPlanName(defaultPlan.name);
        } else {
          setUserPlanId(null);
          setUserPlanName(null);
        }
      }
    };

    determinePlan();
  }, [plans, userDetails?.uid, planDetailsUpdated]);

  const handlePlanSelection = async (plan: Plan, isUserPlan: boolean) => {
    if (isUserPlan && plan.name === 'Custom Plan') {
      dispatch(openModal('CustomPlanPriceModalOpen'));
    } else if (isUserPlan) {
      // Do nothing for current plan
      return;
    } else if (plan.name !== 'Elite') {
      const emailSubject = 'Plan Upgrade Request';
      const userName = userDetails?.display_name || 'Service Provider';
      const businessName = userDetails?.businessProfile?.businessName || 'Not Provided';
      const phoneNumber = userDetails?.phone_number || 'Not Provided';
      const userEmail = userDetails?.email;
      const currentDate = new Date().toLocaleString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });

      const emailText = `We've received a plan upgrade request from the following:
Business Name: ${businessName}
Contact Name: ${userName}
Phone Number: ${phoneNumber}
User Email: ${userEmail}
Current Plan: ${userPlanName || 'current plan'}
Requested Plan: ${plan.name}
Requested on: ${currentDate}

Please review the request and take appropriate action to assist the user.`;

      const mailtoLink = `mailto:support@hosaksham.com?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailText)}`;
      window.open(mailtoLink, '_blank');
    } else {
      setSnackbarMessage('For upgrading plans, please email us at support@hosaksham.com.');
      setSnackbarOpen(true);
    }
  };

  const getCTAButtonText = (plan: Plan, isUserPlan: boolean) => {
    if (isUserPlan) {
      return 'Current Plan';
    }
    if (plan.name === 'Custom Plan') return 'Contact Sales';
    if (plan.name === 'Elite') return 'Start Free';
    return `Sign up for ${plan.name}`;
  };

  const getPriceDisplay = (plan: Plan) => {
    if (plan.price === 0) return '₹0';
    if (plan.price === null) return 'Tailored Pricing';
    return `₹${plan.price.toLocaleString()}`;
  };

  const getBillingPeriodText = (plan: Plan) => {
    if (plan.price === 0) return 'upfront';
    if (plan.price === null) return '';
    if (plan.name === 'Elite Plus') return '+ GST / year';
    if (plan.name === 'Elite Pro') return '+ GST / quarter';
    return '';
  };

  const getPlanBadge = (plan: Plan) => {
    if (plan.name === 'Elite') return 'Pay-As-You-Go';
    if (plan.name === 'Elite Pro') return 'Quarterly Plan';
    if (plan.name === 'Elite Plus') return 'Annual Plan';
    if (plan.name === 'Custom Plan') return 'Customized for your business needs';
    return '';
  };

  const getPlanTagline = (plan: Plan) => {
    if (plan.name === 'Elite') return 'Free to Start';
    if (plan.name === 'Elite Plus') return 'Best Value';
    return '';
  };

  const getIdealForText = (plan: Plan) => {
    if (plan.name === 'Elite')
      return 'Small academies just starting out, looking for zero upfront investment and want to grow with ease.';
    if (plan.name === 'Elite Pro')
      return 'Businesses needing shorter commitments and flexibility without compromising features and grow rapidly.';
    if (plan.name === 'Elite Plus')
      return 'Businesses aiming for growth, needing full flexibility, priority support, and minimal transaction charges.';
    if (plan.name === 'Custom Plan')
      return 'Larger academies or institutions with unique requirements, seeking personalized solutions and dedicated assistance.';
    return '';
  };

  const isPlanHighlighted = (plan: Plan) => {
    return plan.name === 'Elite Plus';
  };

  const isCustomPlan = (plan: Plan) => {
    return plan.name === 'Custom Plan';
  };

  const renderFeatureText = (feature: string) => {
    const isTransactionFee = feature.includes('Transaction Fee');
    const isOnboardingFee = feature.includes('₹10,000 + GST');

    if (isTransactionFee) {
      const parts = feature.split('(');
      if (parts.length > 1) {
        return (
          <PlanFeatureItem>
            <CheckIcon
              sx={{
                color: 'success.main',
                mr: 1,
                fontSize: 18,
                mt: 0.2,
                minWidth: '18px'
              }}
            />
            <Typography
              variant="body2"
              component="div"
              sx={{
                fontSize: '0.875rem',
                fontFamily: "'Inter', sans-serif",
                color: isOnboardingFee ? 'black' : 'text.primary',
                lineHeight: 1.5
              }}
            >
              <span style={{ fontWeight: 500 }}>
                {parts[0]}
              </span>
              <span style={{
                fontSize: '0.8rem',
                color: 'rgba(0, 0, 0, 0.6)',
                display: 'block',
                marginTop: '2px',
                fontWeight: 400
              }}>
                ({parts[1]}
              </span>
            </Typography>
          </PlanFeatureItem>
        );
      }
    }

    return (
      <PlanFeatureItem>
        <CheckIcon
          sx={{
            color: 'success.main',
            mr: 1,
            fontSize: 18,
            mt: 0.2,
            minWidth: '18px'
          }}
        />
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.875rem',
            fontFamily: "'Inter', sans-serif",
            color: 'text.primary',
            fontWeight: 400,
            lineHeight: 1.5
          }}
        >
          {feature}
        </Typography>
      </PlanFeatureItem>
    );
  };

  const getDefaultPlans = () => {
    const defaultPlans = [
      {
        id: 'elite-free',
        planId: 'elite-free',
        plan: 1,
        name: 'Elite Free',
        price: 0,
        features: [
          'Basic features included',
          'Email support',
          'Standard transaction fees',
          'Up to 100 transactions/month'
        ],
        isDefault: true,
      },
      {
        id: 'elite-pro',
        planId: 'elite-pro',
        plan: 2,
        name: 'Elite Pro',
        price: 1999,
        features: [
          'All Elite Free features',
          'Priority support',
          'Reduced transaction fees',
          'Up to 1000 transactions/month',
          'Advanced analytics'
        ],
        graceDays: 14,
        duration: 90
      },
      {
        id: 'elite-plus',
        planId: 'elite-plus',
        plan: 3,
        name: 'Elite Plus',
        price: 4999,
        features: [
          'All Elite Pro features',
          'Dedicated account manager',
          'Custom branding',
          'Unlimited transactions',
          'Priority feature requests'
        ],
        graceDays: 30,
        duration: 365
      },
      {
        id: 'custom-plan',
        planId: 'custom-plan',
        plan: 4,
        name: 'Custom Plan',
        price: null, // Tailored pricing
        features: [
          'Fully customizable features',
          'Enterprise-grade support',
          'Custom integrations',
          'Dedicated onboarding',
          'White-label solution'
        ],
      }
    ];

    setPlans(defaultPlans);
    return defaultPlans;
  };

  return (
    <>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 8, px: 0, pb: 4, fontFamily: "'Inter', sans-serif" }}>
        <Paper
          elevation={0}
          sx={{
            p: 1,
            mb: -2,
            borderRadius: 2,
            backgroundColor: '#f8fafc',
            fontFamily: "'Inter', sans-serif"
          }}
        >
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{ mb: 2, fontFamily: "'Inter', sans-serif" }}
          >
            <Link to="/profile" style={{ textDecoration: 'none', color: 'inherit', fontFamily: "'Inter', sans-serif" }}>
              Profile
            </Link>
            <Typography color="text.primary" sx={{ fontFamily: "'Inter', sans-serif" }}>Plan Details</Typography>
          </Breadcrumbs>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 1,
              fontFamily: "'Inter', sans-serif",
            }}
          >
            My Subscription Plan
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mb: 2,
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Review your current plan and available options
          </Typography>
        </Paper>

        {showExpiryAlert && expiryDate && dueDate && !showAdminAlert && (
          <Alert
            severity="warning"
            sx={{
              mt: 3,
              mb: 2,
              borderRadius: '8px',
              fontFamily: "'Inter', sans-serif",
              '& .MuiAlert-icon': {
                alignItems: 'center'
              },
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Your {userPlanName} plan has expired
                </Typography>
                <Typography variant="body2">
                  Your plan expired on {format(expiryDate, 'dd MMM yyyy')}.
                  Your subscription will fallback to {defaultPlanName} plan on {format(dueDate, 'dd MMM yyyy')}.
                </Typography>
              </Box>
            </Box>
          </Alert>
        )}

        {showAdminAlert && dueDate && (
          <Alert
            severity="error"
            sx={{
              mt: 3,
              mb: 2,
              borderRadius: '8px',
              fontFamily: "'Inter', sans-serif",
              '& .MuiAlert-icon': {
                alignItems: 'center'
              },
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Plan Updated
                </Typography>
                <Typography variant="body2">
                  Grace period for {userPlanName} plan has ended on {format(dueDate, 'dd MMM yyyy')}.
                </Typography>
              </Box>
            </Box>
          </Alert>
        )}

        {error && (
          <Alert
            severity="warning"
            sx={{
              mt: 3,
              mb: 3,
              borderRadius: '8px',
              fontFamily: "'Inter', sans-serif"
            }}
          >
            {error} - Showing default plans instead
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 1,
            mb: 0,
            backgroundColor: '#fafafa',
            backgroundImage: 'radial-gradient(rgba(0, 0, 0, 0.03) 1px, transparent 0)',
            backgroundSize: '20px 20px',
            backgroundPosition: '-10px -10px',
            padding: 0,
            paddingTop: 5,
            paddingBottom: 5,
            borderRadius: 2,
            fontFamily: "'Inter', sans-serif"
          }}
        >
          {plans.map((plan) => {
            const isUserPlan = Boolean(userPlanId && plan.id === userPlanId);
            const showYourPlanBadge = isUserPlan && (!dueDate || !isPast(dueDate));

            return (
              <StyledPlanCard
                key={plan.id}
                sx={{
                  borderColor: 'divider',
                  borderWidth: isPlanHighlighted(plan) ? 2 : 1,
                  backgroundColor: isPlanHighlighted(plan) ? '#f9faff' : 'white',
                  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.06)',
                  padding: theme => theme.spacing(3),
                  ...(isUserPlan ? {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                    overflow: 'hidden',
                  } : {})
                }}
                onClick={() => handlePlanSelection(plan, isUserPlan)}
              >
                {showYourPlanBadge && (
                  <YourPlanBadge>Your Plan</YourPlanBadge>
                )}
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <PlanBadge sx={{
                    backgroundColor: plan.name === 'Elite Plus' ? '#f0f7ff' : '#f5f5f5',
                    color: plan.name === 'Elite Plus' ? '#1e40af' : 'text.secondary',
                    fontWeight: 500,
                    fontSize: '0.75rem'
                  }}>
                    {getPlanBadge(plan)}
                  </PlanBadge>
                </Box>
                <Box sx={{
                  mb: 1,
                  display: 'flex',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: 0.5
                }}>
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: 600,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '1.5rem',
                      letterSpacing: '-0.01em',
                      mr: 1
                    }}
                  >
                    {isCustomPlan(plan) ? 'Tailored Pricing' : plan.name}
                  </Typography>
                  {getPlanTagline(plan) && (
                    <HighlightBadge sx={{
                      mt: 0.3,
                      py: 0.3,
                      px: 1.5
                    }}>
                      {getPlanTagline(plan)}
                    </HighlightBadge>
                  )}
                </Box>
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'baseline' }}>
                  <Typography
                    variant="h5"
                    component="p"
                    sx={{
                      fontWeight: 500,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '2.25rem',
                      letterSpacing: '-0.025em'
                    }}
                  >
                    {isCustomPlan(plan) ? 'Custom Plan' : getPriceDisplay(plan)}
                  </Typography>
                  <Typography
                    variant="body1"
                    component="span"
                    sx={{
                      ml: 1,
                      color: 'text.secondary',
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 400
                    }}
                  >
                    {getBillingPeriodText(plan)}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Button
                    fullWidth
                    variant={isPlanHighlighted(plan) ? "contained" : "outlined"}
                    sx={{
                      mb: 1,
                      py: 1.2,
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      fontFamily: "'Inter', sans-serif",
                      boxShadow: isPlanHighlighted(plan) ? '0 4px 6px rgba(37, 99, 235, 0.25)' : 'none',
                      backgroundColor: isPlanHighlighted(plan) ? '#2563eb' : 'transparent',
                      borderColor: isPlanHighlighted(plan) ? '#2563eb' : 'rgba(0, 0, 0, 0.23)',
                      '&:hover': {
                        backgroundColor: isPlanHighlighted(plan) ? '#1e40af' : 'rgba(0, 0, 0, 0.04)',
                        boxShadow: isPlanHighlighted(plan) ? '0 6px 8px rgba(37, 99, 235, 0.3)' : 'none',
                      }
                    }}
                    onClick={() => handlePlanSelection(plan, isUserPlan)}
                  >
                    {getCTAButtonText(plan, isUserPlan)}
                  </Button>
                </Box>

                <Box sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  minHeight: '230px'
                }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 2,
                      fontWeight: 700,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '0.875rem'
                    }}
                  >
                    {plan.name === "Custom Plan" ? "Features" : "Features"}
                  </Typography>

                  <Box sx={{ mb: 'auto' }}>
                    {plan.features.map((feature, index) => (
                      <React.Fragment key={index}>
                        {renderFeatureText(feature)}
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>

                <Box sx={{
                  mt: 4,
                  pt: 2,
                  borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontWeight: 700,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '0.875rem'
                    }}
                  >
                    Ideal For
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '0.9rem',
                      lineHeight: 1.5
                    }}
                  >
                    {getIdealForText(plan)}
                  </Typography>
                </Box>
              </StyledPlanCard>
            );
          })}
        </Box>
        <Box sx={{
          mt: 3,
          p: 2,
          backgroundColor: '#f8fafc',
          borderRadius: 2,
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
        }}>
          <Typography variant="body2" sx={{ fontFamily: "'Inter', sans-serif", fontWeight: 500, display: 'flex', alignItems: 'center' }}>
            <strong>Note:</strong>&nbsp;Non-UPI end charge is 2.5% across all plans and In case of International payments, the markup will be applied as per the international markup up to {internationalMarkup}.
          </Typography>
        </Box>
      </Container>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          sx={{
            width: '100%',
            fontFamily: "'Inter', sans-serif",
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default SubscriptionPlanPage;